import { AlertTriangle, ChevronRight, CircleAlert, LucideIcon, Wallet } from 'lucide-react';
import { BalanceProps } from './types';
import { cn } from '@/lib/utils';
import { FinanceBadge } from '../../api';
import { formatDate, formatMoney } from '@/lib/formatters';

interface BalanceNextPaymentBadgeConfig {
  className: string;
  icon: LucideIcon;
  label: string;
}

const balanceNextPaymentBadgeConfig: Partial<Record<FinanceBadge, BalanceNextPaymentBadgeConfig>> = {
  EXPECTED: {
    className: 'text-warning',
    icon: AlertTriangle,
    label: 'Ожидается платеж'
  },
  OVERDUE: {
    className: 'text-destructive',
    icon: CircleAlert,
    label: 'Просрочен платеж'
  }
};

const Balance = (props: BalanceProps) => {
  if (props.variant === 'compact') {
    const {
      amount,
      currency,
      isError
    } = props;

    return (
      <div className="flex shrink-0 flex-col items-end gap-1 text-right">
        <p className="font-sans text-sm leading-5 text-muted-foreground-soft">Баланс</p>
        <p className="font-sans text-base font-semibold leading-6 text-foreground">
          {formatMoney({ currency, value: isError ? null : amount })}
        </p>
      </div>
    );
  }

  if (props.variant === 'summary') {
    const {
      amount,
      currency,
      isError,
      onClick
    } = props;

    return (
      <button
        className={cn(
          'group w-full overflow-hidden p-6',
          'text-left',
          'rounded-2xl border border-border-strong bg-surface',
          'transition-colors hover:bg-accent-soft active:bg-accent-soft'
        )}
        onClick={onClick}
        type="button"
      >
        <div>
          <div className="mb-4 flex items-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-lg border border-accent/20 bg-accent/10">
              <Wallet className="size-4 text-accent" />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Баланс</p>
            <ChevronRight className="ml-auto size-4 text-muted-foreground-soft transition-colors group-hover:text-foreground group-active:text-foreground" />
          </div>
          <p className="text-3xl font-bold tracking-tight text-foreground">
            {formatMoney({ currency, value: isError ? null : amount })}
          </p>
        </div>
      </button>
    );
  }

  const {
    availableAmount,
    badge,
    currency,
    isError,
    nextPaymentAmount,
    nextPaymentDate,
    totalAmount,
    totalDueAmount
  } = props;

  const nextPaymentBadgeConfig = badge === 'OVERDUE' ? balanceNextPaymentBadgeConfig[badge] : null;
  const NextPaymentBadgeIcon = nextPaymentBadgeConfig?.icon;
  const resolvedAvailableAmount = availableAmount ?? null;
  const resolvedTotalDueAmount = totalDueAmount ?? totalAmount ?? null;
  const availableAmountLabel = formatMoney({ currency, value: isError ? null : resolvedAvailableAmount });
  const totalDueAmountLabel = formatMoney({ currency, value: isError ? null : resolvedTotalDueAmount });

  return (
    <div className="flex flex-col gap-4 p-5 text-foreground rounded-[24px] border border-border-strong bg-gradient-to-b from-surface-raised to-surface">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Wallet className="size-5 shrink-0 text-accent" />
            <p className="truncate font-sans text-sm font-semibold leading-5 text-muted-foreground-soft">
              Доступный баланс
            </p>
          </div>
          {!isError && nextPaymentBadgeConfig && NextPaymentBadgeIcon ? (
            <div className={cn('flex shrink-0 items-center gap-1', nextPaymentBadgeConfig.className)}>
              <NextPaymentBadgeIcon
                className="size-4"
                strokeWidth={1.8}
              />
              <p className="font-sans text-xs font-semibold leading-4">{nextPaymentBadgeConfig.label}</p>
            </div>
          ) : null}
        </div>
        <p
          className={cn(
            'font-sans text-[28px] font-semibold leading-9',
            'text-foreground'
          )}
        >
          {availableAmountLabel}
        </p>
      </div>
      <div className="h-px w-full bg-border-strong" />
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="font-sans text-xs leading-4 text-muted-foreground-soft">Ближайший платёж</p>
          <p className="truncate font-sans text-sm font-semibold leading-5 text-foreground">
            {isError || nextPaymentAmount == null || !nextPaymentDate
              ? '—'
              : `${formatDate({
                value: nextPaymentDate,
                variant: 'date'
              })} · ${formatMoney({ currency, value: nextPaymentAmount })}`}
          </p>
        </div>
        <div className="h-10 w-px shrink-0 bg-border-strong" />
        <div className="flex shrink-0 flex-col items-end gap-1 text-right whitespace-nowrap">
          <p className="font-sans text-xs leading-4 text-muted-foreground-soft">Всего к оплате</p>
          <p className="font-sans text-sm font-semibold leading-5 text-foreground">
            {totalDueAmountLabel}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Balance;